/**
 * 課題文管理ページ
 * 上: 全課題文のテーブル（目/編集/削除）
 * 下: 健康心理条件を選択して新規作成・編集する編集パネル
 */

import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getConditions,
  getCondition,
  getConditionInstructions,
  getConditionInstruction,
  generateConditionInstruction,
  updateConditionInstruction,
  updateConditionInstructionReviewStatus,
  getAllConditionInstructions,
  deleteConditionInstruction,
} from '../../lib/admin-api';
import type { GeneratePayload } from '../../components/admin/InstructionGenerateDialog';
import { InstructionGenerateDialog } from '../../components/admin/InstructionGenerateDialog';
import AdminLayout from '../../components/admin/AdminLayout';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Button } from '../../components/ui/button';
import { Loader2, FileCheck, Plus, Eye, Edit, Trash2, Play } from 'lucide-react';
import { InstructionEditView } from '../../components/admin/InstructionEditView';
import { ConditionCategoryTable, type CategoryTableData } from '../../components/admin/ConditionCategoryTable';
import { InstructionPreviewBody } from '../../components/InstructionPreviewBody';
import { useToast } from '../../hooks/use-toast';
import { getLegacyCategoryTable } from '../../lib/conditionTableNormalizer';

function formatDate(s: string | Date | null | undefined): string {
  if (!s) return '-';
  const d = new Date(s);
  return isNaN(d.getTime()) ? '-' : d.toLocaleString('ja-JP');
}

function reviewStatusLabel(status: string): string {
  const map: Record<string, string> = {
    draft: '下書き',
    in_review: 'レビュー中',
    approved: '承認済',
  };
  return map[status] ?? status;
}

function ReviewStatusPill({ status }: { status: string }) {
  const tone: Record<string, { bg: string; color: string }> = {
    approved: { bg: '#DCFCE7', color: '#15803D' },
    in_review: { bg: '#FEF3C7', color: '#B45309' },
    draft: { bg: '#F4F4F5', color: '#52525B' },
  };
  const t = tone[status] ?? tone.draft;
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 96,
        padding: '7px 10px',
        borderRadius: 8,
        background: t.bg,
        color: t.color,
        border: `1px solid ${t.color}`,
        fontSize: 13,
        fontWeight: 800,
        whiteSpace: 'nowrap',
      }}
    >
      {reviewStatusLabel(status)}
    </span>
  );
}

function InterviewModePill({ isIntake }: { isIntake: boolean }) {
  const t = isIntake
    ? { bg: '#D1FAE5', color: '#047857', label: '初診時医療面接' }
    : { bg: '#CFFAFE', color: '#0E7490', label: '医療面接' };
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 96,
        padding: '7px 10px',
        borderRadius: 8,
        background: t.bg,
        color: t.color,
        border: `1px solid ${t.color}`,
        fontSize: 13,
        fontWeight: 800,
        whiteSpace: 'nowrap',
      }}
    >
      {t.label}
    </span>
  );
}

type AllInstruction = {
  id: number;
  condition_id: number;
  gender?: string;
  age_range?: string;
  condition_is_intake?: boolean;
  review_status?: string;
  created?: string;
  updated?: string;
  instruction_data?: Record<string, unknown>;
  resolved?: {
    summary: string;
    targetCategories: string[];
    targetCategoryLabels?: Record<string, string>;
    tasks: Array<{ no: number; title?: string; details?: string[] }>;
  };
};

export default function InstructionsList() {
  const initialConditionId = typeof window !== 'undefined'
    ? (new URLSearchParams(window.location.search).get('conditionId') ?? '')
    : '';
  const [conditionId, setConditionId] = useState<string>(initialConditionId);
  const [selectedInstructionId, setSelectedInstructionId] = useState<number | null>(null);
  useEffect(() => {
    if (initialConditionId) {
      setTimeout(() => {
        document.getElementById('instruction-edit-section')?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  }, [initialConditionId]);
  const [isGenerateDialogOpen, setIsGenerateDialogOpen] = useState(false);
  const [viewInstruction, setViewInstruction] = useState<AllInstruction | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AllInstruction | null>(null);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const { data: allData, isLoading: isAllLoading } = useQuery({
    queryKey: ['admin', 'all-condition-instructions'],
    queryFn: () => getAllConditionInstructions(),
  });
  const allInstructions: AllInstruction[] = allData?.instructions ?? [];

  const { data: conditionsData } = useQuery({
    queryKey: ['admin', 'conditions'],
    queryFn: () => getConditions(),
  });
  const conditions = conditionsData?.conditions ?? [];

  const selectedId = conditionId ? parseInt(conditionId, 10) : null;
  const selectedConditionSummary = conditions.find((c: { id: number }) => c.id === selectedId) ?? null;
  const { data: instructionsData, isLoading } = useQuery({
    queryKey: ['admin', 'condition-instructions', selectedId],
    queryFn: () => getConditionInstructions(selectedId!),
    enabled: !!selectedId,
  });
  const instructions = instructionsData?.instructions ?? [];

  const { data: selectedConditionData, isLoading: isConditionLoading } = useQuery({
    queryKey: ['admin', 'condition', selectedId],
    queryFn: () => getCondition(selectedId!),
    enabled: !!selectedId,
  });
  const selectedCondition = selectedConditionData?.condition ?? selectedConditionSummary ?? null;
  const categoryTable = getLegacyCategoryTable(selectedCondition?.condition_data) as CategoryTableData | null;

  const { data: selectedInstructionData, isLoading: isSelectedInstructionLoading } = useQuery({
    queryKey: ['admin', 'condition-instruction', selectedInstructionId],
    queryFn: () => getConditionInstruction(selectedInstructionId!),
    enabled: !!selectedInstructionId,
  });
  const selectedInstruction = selectedInstructionData?.instruction ?? null;

  const generateMutation = useMutation({
    mutationFn: ({ conditionId: cid, selectedCells, lvSelections }: GeneratePayload) => {
      return generateConditionInstruction(cid, selectedCells, lvSelections);
    },
    onSuccess: (data: { instruction?: { id: number } }, variables: GeneratePayload) => {
      const id = data?.instruction?.id;
      if (id) {
        setSelectedInstructionId(id);
        setIsGenerateDialogOpen(false);
        setConditionId(String(variables.conditionId));
        queryClient.invalidateQueries({ queryKey: ['admin', 'condition-instructions', variables.conditionId] });
        queryClient.invalidateQueries({ queryKey: ['admin', 'condition-instructions', selectedId] });
        queryClient.invalidateQueries({ queryKey: ['admin', 'all-condition-instructions'] });
        toast({ title: '課題文を生成しました' });
      }
    },
    onError: (error: Error) => {
      toast({ title: '課題文生成エラー', description: error.message, variant: 'destructive' });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => deleteConditionInstruction(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'all-condition-instructions'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'condition-instructions', selectedId] });
      if (selectedInstructionId && deleteTarget?.id === selectedInstructionId) {
        setSelectedInstructionId(null);
      }
      setDeleteTarget(null);
      toast({ title: '課題文を削除しました' });
    },
    onError: (error: Error) => {
      toast({ title: '削除エラー', description: error.message, variant: 'destructive' });
    },
  });

  const handleOpenCreateDialog = () => {
    if (!selectedId) return;
    setIsGenerateDialogOpen(true);
  };

  const handleGenerateConfirm = (payload: GeneratePayload) => {
    generateMutation.mutate(payload);
  };

  const handleUpdate = async (id: number, data: object) => {
    await updateConditionInstruction(id, data);
    queryClient.invalidateQueries({ queryKey: ['admin', 'condition-instruction', id] });
    queryClient.invalidateQueries({ queryKey: ['admin', 'condition-instructions', selectedId] });
    queryClient.invalidateQueries({ queryKey: ['admin', 'all-condition-instructions'] });
    toast({ title: '課題文を保存しました' });
  };

  const handleUpdateReviewStatus = async (id: number, status: 'approved' | 'draft' | 'in_review') => {
    await updateConditionInstructionReviewStatus(id, status);
    queryClient.invalidateQueries({ queryKey: ['admin', 'condition-instruction', id] });
    queryClient.invalidateQueries({ queryKey: ['admin', 'condition-instructions', selectedId] });
    queryClient.invalidateQueries({ queryKey: ['admin', 'all-condition-instructions'] });
    toast({ title: status === 'approved' ? 'レビュー完了にしました' : 'レビュー中にしました' });
  };

  const handleEditFromList = (i: AllInstruction) => {
    setConditionId(String(i.condition_id));
    setSelectedInstructionId(i.id);
    setTimeout(() => {
      document.getElementById('instruction-edit-section')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const handleBackToList = () => setSelectedInstructionId(null);

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-4xl font-bold tracking-tight text-gray-900 flex items-center gap-2">
              <FileCheck className="w-8 h-8" />
              課題文管理
            </h1>
            <p className="text-base text-gray-500 mt-2">課題文を管理します</p>
          </div>
        </div>

        {/* 上: 全課題文のテーブル */}
        <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
          <div className="text-2xl font-bold text-gray-900 mb-4">課題文一覧</div>
          {isAllLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
          ) : allInstructions.length === 0 ? (
            <p className="py-12 text-center text-base text-gray-500">
              課題文がまだ登録されていません。下の「健康心理条件から編集」から新規作成してください。
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-b border-gray-200 hover:bg-transparent">
                  <TableHead className="text-base font-bold text-gray-700 w-20">ID</TableHead>
                  <TableHead className="text-base font-bold text-gray-700 text-center">健康心理条件ID</TableHead>
                  <TableHead className="text-base font-bold text-gray-700 text-center">性別/年代</TableHead>
                  <TableHead className="text-base font-bold text-gray-700 text-center">面接モード</TableHead>
                  <TableHead className="text-base font-bold text-gray-700 text-center">レビュー状態</TableHead>
                  <TableHead className="text-base font-bold text-gray-700 text-center">作成日時</TableHead>
                  <TableHead className="text-base font-bold text-gray-700 text-center">更新日時</TableHead>
                  <TableHead className="text-base font-bold text-gray-700 text-center">面接</TableHead>
                  <TableHead className="text-base font-bold text-gray-700 w-40 text-center">操作</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {allInstructions.map((i) => (
                  <TableRow key={i.id} className="border-b border-gray-100 hover:bg-gray-50/50">
                    <TableCell className="py-4 text-base font-medium text-gray-900 tabular-nums">{i.id}</TableCell>
                    <TableCell className="py-4 text-center text-base font-medium text-gray-900 tabular-nums">{i.condition_id}</TableCell>
                    <TableCell className="py-4 text-center text-base font-medium text-gray-900">{i.gender ?? '-'} / {i.age_range ?? '-'}</TableCell>
                    <TableCell className="py-4 text-center">
                      <InterviewModePill isIntake={!!i.condition_is_intake} />
                    </TableCell>
                    <TableCell className="py-4 text-center">
                      <ReviewStatusPill status={i.review_status ?? 'draft'} />
                    </TableCell>
                    <TableCell className="py-4 text-center text-base font-medium text-gray-900 tabular-nums">{formatDate(i.created)}</TableCell>
                    <TableCell className="py-4 text-center text-base font-medium text-gray-900 tabular-nums">{formatDate(i.updated)}</TableCell>
                    <TableCell className="py-4 text-center">
                      {i.review_status === 'approved' && (
                        <Button
                          className="bg-gray-900 hover:bg-gray-800 text-white rounded-lg h-11 px-5 font-bold shadow-sm whitespace-nowrap"
                          onClick={() => { window.location.href = `/scenario-preview?instructionId=${i.id}`; }}
                          title="シナリオを選択して始める"
                        >
                          <Play className="w-4 h-4 mr-1.5" />
                          シナリオを選択して始める
                        </Button>
                      )}
                    </TableCell>
                    <TableCell className="py-4">
                      <div className="flex gap-2 justify-center">
                        <button
                          type="button"
                          onClick={() => setViewInstruction(i)}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-blue-200 text-blue-600 hover:bg-blue-50"
                          aria-label="プレビュー"
                          title="プレビュー"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleEditFromList(i)}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50"
                          aria-label="編集"
                          title="編集"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => setDeleteTarget(i)}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-red-200 text-red-600 hover:bg-red-50"
                          aria-label="削除"
                          title="削除"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>

        {/* 下: 健康心理条件から編集・新規作成 */}
        <div id="instruction-edit-section" className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
          <div className="text-2xl font-bold text-gray-900 mb-2">健康心理条件から編集</div>
          <p className="text-base text-gray-500 mb-4">
            健康心理条件を選択して課題文の編集・新規作成ができます。
          </p>
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <span className="text-base font-bold text-gray-700 whitespace-nowrap">健康心理条件:</span>
              <Select value={conditionId} onValueChange={(v) => { setConditionId(v); setSelectedInstructionId(null); }}>
                <SelectTrigger className="h-11 rounded-xl border-gray-200 w-80">
                  <SelectValue placeholder="条件を選択してください" />
                </SelectTrigger>
                <SelectContent>
                  {conditions.map((c: { id: number; gender?: string; age_range?: string }) => (
                    <SelectItem key={c.id} value={String(c.id)}>
                      ID: {c.id} ({c.gender ?? '-'}/{c.age_range ?? '-'})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {conditions.length === 0 && (
              <p className="text-base text-amber-600">
                健康心理条件が登録されていません。先に「健康心理条件管理」で条件を登録してください。
              </p>
            )}
            {conditions.length > 0 && !selectedId && (
              <p className="text-base text-gray-500">
                上記から健康心理条件を選択してください。
              </p>
            )}

            {selectedId && (
              <div className="flex flex-col gap-6">
                <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
                  <div className="text-2xl font-bold text-gray-900 mb-2">健康心理条件テーブル</div>
                  <p className="text-base text-gray-500 mb-2">
                    条件を確認しながら、下の一覧で課題文をレビュー・編集できます。
                  </p>
                  <div className="text-base text-gray-500 mb-4">
                    ID: {selectedCondition?.id ?? '-'} / {selectedCondition?.gender ?? '-'} / {selectedCondition?.age_range ?? '-'}
                  </div>
                  {isConditionLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                    </div>
                  ) : categoryTable ? (
                    <ConditionCategoryTable
                      categoryTable={categoryTable}
                      maxHeightClassName="max-h-[min(480px,55vh)]"
                    />
                  ) : (
                    <p className="text-base text-gray-500">この健康心理条件にはテーブルデータがありません。</p>
                  )}
                </div>

                <div className="min-w-0">
                  {selectedInstructionId ? (
                    isSelectedInstructionLoading ? (
                      <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
                        <div className="flex items-center justify-center py-16">
                          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                        </div>
                      </div>
                    ) : selectedInstruction ? (
                      <InstructionEditView
                        instruction={selectedInstruction}
                        onBack={handleBackToList}
                        onUpdate={handleUpdate}
                        onUpdateReviewStatus={handleUpdateReviewStatus}
                        categoryTable={categoryTable}
                        conditionData={selectedCondition?.condition_data ?? null}
                      />
                    ) : (
                      <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
                        <div className="py-12 text-center text-base text-gray-500">
                          課題文の取得に失敗しました。
                        </div>
                      </div>
                    )
                  ) : (
                    <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
                      <div className="text-2xl font-bold text-gray-900 mb-4">この条件の課題文</div>
                      <div className="space-y-4">
                        {isLoading ? (
                          <div className="flex items-center justify-center py-12">
                            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                          </div>
                        ) : instructions.length === 0 ? (
                          <div className="py-12 text-center space-y-4">
                            <p className="text-base text-gray-500">この条件に紐づく課題文はありません。</p>
                            <Button
                              onClick={handleOpenCreateDialog}
                              disabled={generateMutation.isPending}
                              className="bg-gray-900 hover:bg-gray-800 text-white rounded-lg h-11 px-5 font-bold shadow-sm"
                            >
                              {generateMutation.isPending ? (
                                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                              ) : (
                                <Plus className="w-4 h-4 mr-2" />
                              )}
                              新規作成
                            </Button>
                          </div>
                        ) : (
                          <>
                            <Table>
                              <TableHeader>
                                <TableRow className="border-b border-gray-200 hover:bg-transparent">
                                  <TableHead className="text-base font-bold text-gray-700 w-20">ID</TableHead>
                                  <TableHead className="text-base font-bold text-gray-700">レビュー状態</TableHead>
                                  <TableHead className="text-base font-bold text-gray-700">作成日時</TableHead>
                                  <TableHead className="text-base font-bold text-gray-700">更新日時</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {instructions.map((i: { id: number; review_status?: string; created?: string; updated?: string }) => (
                                  <TableRow
                                    key={i.id}
                                    className="border-b border-gray-100 cursor-pointer hover:bg-gray-50/50"
                                    onClick={() => setSelectedInstructionId(i.id)}
                                  >
                                    <TableCell className="py-4 text-base font-medium text-gray-900 tabular-nums">{i.id}</TableCell>
                                    <TableCell className="py-4">
                                      <ReviewStatusPill status={i.review_status ?? 'draft'} />
                                    </TableCell>
                                    <TableCell className="py-4 text-base font-medium text-gray-900 tabular-nums">{formatDate(i.created)}</TableCell>
                                    <TableCell className="py-4 text-base font-medium text-gray-900 tabular-nums">{formatDate(i.updated)}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                            <div className="border-t pt-4">
                              <Button variant="outline" size="sm" onClick={handleOpenCreateDialog} disabled={generateMutation.isPending} className="h-9 rounded-lg border-gray-200">
                                {generateMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                                新規作成
                              </Button>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        <InstructionGenerateDialog
          open={isGenerateDialogOpen}
          onOpenChange={setIsGenerateDialogOpen}
          conditions={conditions}
          currentConditionId={selectedId}
          onConfirm={handleGenerateConfirm}
          isGenerating={generateMutation.isPending}
        />
      </div>

      {/* プレビューダイアログ（シナリオ選択後の画面と同じ見た目） */}
      <Dialog open={viewInstruction !== null} onOpenChange={(open) => !open && setViewInstruction(null)}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>課題文プレビュー (ID: {viewInstruction?.id})</DialogTitle>
          </DialogHeader>
          <InstructionPreviewBody
            summary={viewInstruction?.resolved?.summary}
            targetCategories={viewInstruction?.resolved?.targetCategories ?? []}
            categoryLabels={viewInstruction?.resolved?.targetCategoryLabels}
            tasks={viewInstruction?.resolved?.tasks ?? []}
          />
        </DialogContent>
      </Dialog>

      {/* 削除確認ダイアログ */}
      <AlertDialog open={deleteTarget !== null} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>課題文を削除しますか？</AlertDialogTitle>
            <AlertDialogDescription>
              課題文ID {deleteTarget?.id} を削除します。この操作は取り消せません。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>キャンセル</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
            >
              削除
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
}
